// ✅ src/pages/OrderManagement/TaxReport.jsx  (FULL FILE - UPDATED)
// ✅ Uses safe localStorage helper
// ✅ Adds finYearId, finYear, branchId, branchType, loginUserId
// ✅ Adds commonHeaders in all API calls
// ✅ Backend mount:
//   app.use("/api/taxReport", taxreport);
//
// Endpoints expected:
//   GET  http://nissi-india.com/api/taxReport/list?srcId=...&contractName=...
//   GET  http://nissi-india.com/api/taxReport/autocomplete/srcid?prefix=...
//   GET  http://nissi-india.com/api/taxReport/autocomplete/contract?prefix=...

import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaEye, FaChevronUp, FaChevronDown } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/taxReport";

const toStr = (v, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  return String(v).trim();
};

const safe = (v) => toStr(v);

const toInt = (v, fallback = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

export default function TaxReport() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    srcId: "",
    contractName: "",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // autocomplete state
  const [srcSug, setSrcSug] = useState([]);
  const [conSug, setConSug] = useState([]);
  const [openSug, setOpenSug] = useState({ src: false, con: false });

  const srcWrapRef = useRef(null);
  const conWrapRef = useRef(null);

  const columns = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0] || {});
  }, [rows]);

  const fetchList = async (payloadFilters) => {
    setLoading(true);
    try {
      const params = {
        srcId: payloadFilters?.srcId ?? filters.srcId,
        contractName: payloadFilters?.contractName ?? filters.contractName,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setRows(Array.isArray(data) ? data : []);

      if (data.length) toast.success(`Loaded ${data.length} record(s)`);
      else toast.info("No records found");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // initial load = View All
  useEffect(() => {
    fetchList({ srcId: "", contractName: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close suggestions on outside click
  useEffect(() => {
    const onDoc = (e) => {
      const inSrc = srcWrapRef.current?.contains(e.target);
      const inCon = conWrapRef.current?.contains(e.target);
      if (!inSrc && !inCon) setOpenSug({ src: false, con: false });
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleChange = (field, value) => {
    setFilters((p) => ({ ...p, [field]: value }));
  };

  // debounce helper
  const useDebounce = (value, delay = 250) => {
    const [deb, setDeb] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDeb(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return deb;
  };

  const debSrc = useDebounce(filters.srcId, 250);
  const debCon = useDebounce(filters.contractName, 250);

  // autocomplete fetch: srcId
  useEffect(() => {
    const run = async () => {
      const p = safe(debSrc).trim();
      if (!p) {
        setSrcSug([]);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/autocomplete/srcid`, {
          params: { prefix: p },
          headers: commonHeaders,
        });
        setSrcSug(res.data?.data || []);
      } catch (e) {
        setSrcSug([]);
      }
    };
    run();
  }, [debSrc, commonHeaders]);

  // autocomplete fetch: contract
  useEffect(() => {
    const run = async () => {
      const p = safe(debCon).trim();
      if (!p) {
        setConSug([]);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/autocomplete/contract`, {
          params: { prefix: p },
          headers: commonHeaders,
        });
        setConSug(res.data?.data || []);
      } catch (e) {
        setConSug([]);
      }
    };
    run();
  }, [debCon, commonHeaders]);

  const handleSearch = () => {
    fetchList();
  };

  const handleViewAll = () => {
    const next = { srcId: "", contractName: "" };
    setFilters(next);
    setOpenSug({ src: false, con: false });
    fetchList(next);
  };

  const renderCell = (val) => {
    if (val === null || val === undefined) return "";
    return String(val);
  };

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TAX REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* header + toggle */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Taxes
            </h1>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* filters */}
          {showFilters && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* SRC ID with suggestions */}
                <div ref={srcWrapRef} className="relative">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    SRC Id No
                  </label>
                  <input
                    value={filters.srcId}
                    onChange={(e) => handleChange("srcId", e.target.value)}
                    onFocus={() => setOpenSug((p) => ({ ...p, src: true }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Type SRC Id..."
                    autoComplete="off"
                  />
                  {openSug.src && srcSug.length > 0 && (
                    <div
                      className={`absolute z-50 mt-1 w-full rounded-lg border shadow-lg max-h-56 overflow-auto ${
                        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {srcSug.slice(0, 30).map((s, idx) => (
                        <button
                          key={`${s}-${idx}`}
                          type="button"
                          onClick={() => {
                            handleChange("srcId", s);
                            setOpenSug((p) => ({ ...p, src: false }));
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            isDark ? "hover:bg-gray-700 text-white" : "hover:bg-gray-100 text-gray-900"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contract with suggestions */}
                <div ref={conWrapRef} className="relative">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contract Name
                  </label>
                  <input
                    value={filters.contractName}
                    onChange={(e) => handleChange("contractName", e.target.value)}
                    onFocus={() => setOpenSug((p) => ({ ...p, con: true }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Type contract name..."
                    autoComplete="off"
                  />
                  {openSug.con && conSug.length > 0 && (
                    <div
                      className={`absolute z-50 mt-1 w-full rounded-lg border shadow-lg max-h-56 overflow-auto ${
                        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {conSug.slice(0, 30).map((s, idx) => (
                        <button
                          key={`${s}-${idx}`}
                          type="button"
                          onClick={() => {
                            handleChange("contractName", s);
                            setOpenSug((p) => ({ ...p, con: false }));
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            isDark ? "hover:bg-gray-700 text-white" : "hover:bg-gray-100 text-gray-900"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* actions */}
              <div className="flex justify-center items-center gap-6 mb-8">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? `${buttonClass} ${hoverClass} text-white`
                      : `${buttonClass} ${hoverClass} text-white`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSearch />
                  {loading ? "Loading..." : "Search"}
                </button>

                <button
                  onClick={handleViewAll}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-600 hover:bg-gray-700 text-white"
                      : "bg-gray-500 hover:bg-gray-600 text-white"
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaEye />
                  View All
                </button>
              </div>
            </>
          )}

          {/* table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    {(columns.length ? columns : ["No data"]).map((c) => (
                      <th
                        key={c}
                        className="px-4 py-4 text-center font-semibold border-r border-white/20 whitespace-nowrap"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.length === 0 ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-white"}>
                      <td
                        colSpan={Math.max(columns.length, 1)}
                        className={`px-4 py-6 text-center ${
                          isDark ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        {loading ? "Loading..." : "No records"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        {columns.map((c) => (
                          <td
                            key={c}
                            className={`px-4 py-3 text-center border-r whitespace-nowrap ${
                              isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                            }`}
                          >
                            {renderCell(r[c])}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ToastContainer position="bottom-right" theme={isDark ? "dark" : "light"} />
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
}